import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";

import Login from "./pages/Login";
import OperativaDashboard from "./pages/OperativaDashboard";
import CreateRequest from "./pages/CreateRequest";
import CreateEvento from "./pages/CreateEvento";
import RequestDetail from "./pages/RequestDetail";
import PartnerDashboard from "./pages/PartnerDashboard";
import PartnersPage from "./pages/PartnersPage";
import StatsPage from "./pages/StatsPage";
import CalendarPage from "./pages/CalendarPage";
import StaffPage from "./pages/StaffPage";
import PartnerReportPage from "./pages/PartnerReportPage";
import ReportRichiedentiPage from "./pages/ReportRichiedentiPage";
import PatientsPage from "./pages/PatientsPage";
import EntitiesPage from "./pages/EntitiesPage";
import RichiedentiPage from "./pages/RichiedentiPage";
import ResetPassword from "./pages/ResetPassword";
import PublicCorsa from "./pages/PublicCorsa";
import PublicPreventivo from "./pages/PublicPreventivo";
import PartnerArchivePage from "./pages/PartnerArchivePage";
import ImportPage from "./pages/ImportPage";
import OptionsPage from "./pages/OptionsPage";
import RequestAuditPage from "./pages/RequestAuditPage";
import EmailLogPage from "./pages/EmailLogPage";
import InsightsPage from "./pages/InsightsPage";
import FormazionePage from "./pages/FormazionePage";
import FormazioneClientPage from "./pages/FormazioneClientPage";
import PartnerListsPage from "./pages/PartnerListsPage";
import PreventiviPage from "./pages/PreventiviPage";
import PreventivoFormPage from "./pages/PreventivoFormPage";
import PreventivoEventoFormPage from "./pages/PreventivoEventoFormPage";
import TariffePage from "./pages/TariffePage";
import StrutturaLogin from "./pages/StrutturaLogin";
import PortalHome from "./pages/PortalHome";
import PortalNewRequest from "./pages/PortalNewRequest";
import PortalRequestView from "./pages/PortalRequestView";
import StrutturaRequestPage from "./pages/StrutturaRequestPage";
import StruttureLogPage from "./pages/StruttureLogPage";
import StruttureAccessiPage from "./pages/StruttureAccessiPage";

const homeOf = (u) => (u.role === "admin" ? "/operativa" : u.role === "struttura" ? "/strutture" : "/partner");

const FullLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="h-7 w-7 animate-spin text-primary" />
  </div>
);

function Protected({ role, adminOnly, children }) {
  const { user, checking } = useAuth();
  if (checking) return <FullLoader />;
  if (!user) return <Navigate to={role === "struttura" ? "/strutture/login" : "/login"} replace />;
  if (role && user.role !== role) {
    return <Navigate to={homeOf(user)} replace />;
  }
  if (adminOnly && user.staff_role === "operatore") {
    return <Navigate to="/operativa" replace />;
  }
  return children;
}

function RootRedirect() {
  const { user, checking } = useAuth();
  if (checking) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeOf(user)} replace />;
}

function LoginRoute() {
  const { user, checking } = useAuth();
  if (checking) return <FullLoader />;
  if (user) return <Navigate to={homeOf(user)} replace />;
  return <Login />;
}

function StrutturaLoginRoute() {
  const { user, checking } = useAuth();
  if (checking) return <FullLoader />;
  if (user?.role === "struttura") return <Navigate to="/strutture" replace />;
  return <StrutturaLogin />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/corsa/:token" element={<PublicCorsa />} />
            <Route path="/preventivo/:token" element={<PublicPreventivo />} />
            <Route path="/preventivo-evento/:token" element={<PublicPreventivo evento />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/operativa" element={<Protected role="admin"><OperativaDashboard /></Protected>} />
            <Route path="/operativa/nuova" element={<Protected role="admin"><CreateRequest /></Protected>} />
            <Route path="/operativa/nuovo-evento" element={<Protected role="admin"><CreateEvento /></Protected>} />
            <Route path="/operativa/evento/:id/modifica" element={<Protected role="admin"><CreateEvento /></Protected>} />
            <Route path="/operativa/pazienti" element={<Protected role="admin"><PatientsPage /></Protected>} />
            <Route path="/operativa/enti" element={<Protected role="admin"><EntitiesPage /></Protected>} />
            <Route path="/operativa/richiedenti" element={<Protected role="admin"><RichiedentiPage /></Protected>} />
            <Route path="/operativa/richiesta/:id/modifica" element={<Protected role="admin"><CreateRequest /></Protected>} />
            <Route path="/operativa/statistiche" element={<Protected role="admin"><StatsPage /></Protected>} />
            <Route path="/operativa/calendario" element={<Protected role="admin"><CalendarPage /></Protected>} />
            <Route path="/operativa/report" element={<Protected role="admin"><PartnerReportPage /></Protected>} />
            <Route path="/operativa/report-richiedenti" element={<Protected role="admin"><ReportRichiedentiPage /></Protected>} />
            <Route path="/operativa/staff" element={<Protected role="admin" adminOnly><StaffPage /></Protected>} />
            <Route path="/operativa/partner" element={<Protected role="admin" adminOnly><PartnersPage /></Protected>} />
            <Route path="/operativa/liste-partner" element={<Protected role="admin" adminOnly><PartnerListsPage /></Protected>} />
            <Route path="/operativa/richiesta/:id" element={<Protected role="admin"><RequestDetail /></Protected>} />
            <Route path="/partner" element={<Protected role="partner"><PartnerDashboard /></Protected>} />
            <Route path="/partner/archivio" element={<Protected role="partner"><PartnerArchivePage /></Protected>} />
            <Route path="/operativa/importa" element={<Protected role="admin"><ImportPage /></Protected>} />
            <Route path="/operativa/opzioni" element={<Protected role="admin"><OptionsPage /></Protected>} />
            <Route path="/operativa/attivita" element={<Protected role="admin"><OptionsPage only="attivita" /></Protected>} />
            <Route path="/operativa/causali" element={<Protected role="admin"><OptionsPage only="causale" /></Protected>} />
            <Route path="/operativa/log-corse" element={<Protected role="admin"><RequestAuditPage /></Protected>} />
            <Route path="/operativa/log-email" element={<Protected role="admin" adminOnly><EmailLogPage /></Protected>} />
            <Route path="/operativa/insights" element={<Protected role="admin" adminOnly><InsightsPage /></Protected>} />
            <Route path="/operativa/formazione" element={<Protected role="admin"><FormazionePage /></Protected>} />
            <Route path="/operativa/formazione/:id" element={<Protected role="admin"><FormazioneClientPage /></Protected>} />
            <Route path="/operativa/amministrazione/preventivi" element={<Protected role="admin" adminOnly><PreventiviPage /></Protected>} />
            <Route path="/operativa/amministrazione/preventivi/:id" element={<Protected role="admin" adminOnly><PreventivoFormPage /></Protected>} />
            <Route path="/operativa/amministrazione/preventivi-eventi/:id" element={<Protected role="admin" adminOnly><PreventivoEventoFormPage /></Protected>} />
            <Route path="/operativa/amministrazione/tariffe" element={<Protected role="admin" adminOnly><TariffePage /></Protected>} />
            <Route path="/strutture/login" element={<StrutturaLoginRoute />} />
            <Route path="/strutture" element={<Protected role="struttura"><PortalHome /></Protected>} />
            <Route path="/strutture/nuova" element={<Protected role="struttura"><PortalNewRequest /></Protected>} />
            <Route path="/strutture/richiesta/:id" element={<Protected role="struttura"><PortalRequestView /></Protected>} />
            <Route path="/operativa/strutture/richieste/:id" element={<Protected role="admin"><StrutturaRequestPage /></Protected>} />
            <Route path="/operativa/log-strutture" element={<Protected role="admin"><StruttureLogPage /></Protected>} />
            <Route path="/operativa/strutture-accessi" element={<Protected role="admin" adminOnly><StruttureAccessiPage /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
